/**
 * API 接口封装 - 所有接口都封装成 async 函数
 * 使用原生 fetch API，不依赖 axios
 */

import { API_BASE_URL } from '@/config'

/**
 * 统一的请求处理函数
 * @param {string} url - 请求地址
 * @param {Object} options - fetch 配置项
 * @returns {Promise} 响应数据
 */
async function request(url, options = {}) {
  try {
    const response = await fetch(API_BASE_URL + url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    })
    
    const data = await response.json()
    
    // 根据后端返回的 code 判断请求是否成功
    // 支持 code === 0 或 code === 200
    if (data.code === 0 || data.code === 200) {
      return data
    } else {
      throw new Error(data.message || '请求失败')
    }
  } catch (error) {
    console.error('请求错误:', error)
    throw error
  }
}

/**
 * 1. 创建工作组（一级节点）
 * @param {Object} params - 参数对象
 * @param {string} params.user_operator_id - 用户ID
 * @param {string} params.group_name - 工作组名称
 * @returns {Promise} 响应数据
 */
export async function createGroup(params) {
  return await request('/cdtdmExcel/createGroup', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 2. 查询所在工作组列表
 * @param {Object} params - 参数对象
 * @param {string} params.user_operator_id - 用户ID
 * @returns {Promise} 响应数据
 */
export async function getGroupList(params) {
  return await request('/cdtdmExcel/getGroupList', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 3. 组新建数据集（二级节点）
 * @param {Object} params - 参数对象
 * @param {number} params.group_id - 工作组ID
 * @param {string} params.user_operator_id - 用户ID
 * @param {string} params.database_name - 数据集名称
 * @returns {Promise} 响应数据
 */
export async function createDatabase(params) {
  return await request('/cdtdmExcel/share/createDatabase', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 4. 组内数据集列表查询
 * @param {Object} params - 参数对象
 * @param {number} params.group_id - 工作组ID
 * @returns {Promise} 响应数据
 */
export async function getShareDatabaseList(params) {
  const queryString = new URLSearchParams(params).toString()
  return await request(`/cdtdmExcel/share/getShareDatabaseList?${queryString}`, {
    method: 'GET'
  })
}

/**
 * 5. 组内修改数据集名称
 * @param {Object} params - 参数对象
 * @param {number} params.share_database_id - 数据集ID
 * @param {string} params.database_name - 新的数据集名称
 * @returns {Promise} 响应数据
 */
export async function updateDatabaseName(params) {
  return await request('/cdtdmExcel/share/updateDatabaseName', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 6. 删除数据集
 * @param {Object} params - 参数对象
 * @param {number} params.share_database_id - 数据集ID
 * @returns {Promise} 响应数据
 */
export async function deleteDatabase(params) {
  return await request('/cdtdmExcel/share/deleteDatabase', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 7. 获取工作组用户列表（返回所有用户，用isJoin标记）
 * @param {Object} params - 参数对象
 * @param {number} params.group_id - 工作组ID
 * @returns {Promise} 响应数据
 */
export async function getJoinGroupUserList(params) {
  return await request('/cdtdmExcel/getJoinGroupUserList', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 8. 批量添加工作组成员
 * @param {Object} params - 参数对象
 * @param {number} params.group_id - 工作组ID
 * @param {Array<number>} params.user_ids - 用户ID列表（数字数组）
 * @returns {Promise} 响应数据
 */
export async function joinGroupMembers(params) {
  return await request('/cdtdmExcel/joinGroupMembers', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 9. 批量删除工作组成员
 * @param {Object} params - 参数对象
 * @param {number} params.group_id - 工作组ID
 * @param {Array<number>} params.user_ids - 用户ID列表（数字数组）
 * @returns {Promise} 响应数据
 */
export async function delGroupMembers(params) {
  return await request('/cdtdmExcel/delGroupMembers', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 10. 初始化演示用户数据
 * @returns {Promise} 响应数据
 */
export async function initDemoUsers() {
  return await request('/cdtdmExcel/member/initDemoUsers', {
    method: 'POST'
  })
}

/**
 * 11. 查询共享全部数据解密版
 * @param {Object} params - 参数对象
 * @param {number} params.database_id - 数据集ID
 * @returns {Promise} 响应数据
 */
export async function getDataDetailById(params) {
  const queryString = new URLSearchParams(params).toString()
  return await request(`/cdtdmExcel/share/getDataDetailById?${queryString}`, {
    method: 'GET'
  })
}

/**
 * 12. 共享更新表格数据并解锁
 * @param {Object} params - 参数对象
 * @param {number} params.value_id - 数据ID
 * @param {string} params.user_operator_id - 用户操作ID
 * @param {Object} params.excel_value - 表格数据对象
 * @returns {Promise} 响应数据
 */
export async function updateSingleDataById(params) {
  return await request('/cdtdmExcel/share/updateSingleDataById', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 13. 修改文本并解锁
 * @param {Object} params - 参数对象
 * @param {number} params.value_id - 数据ID
 * @param {string} params.user_operator_id - 用户操作ID
 * @param {string} params.value - 文本的HTML内容
 * @returns {Promise} 响应数据
 */
export async function updateTXT(params) {
  return await request('/cdtdmExcel/share/updateTXT', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 14. 轮询查询当前共享数据集是否为最新版本
 * @param {Object} params - 参数对象
 * @param {Array} params.share_values_version - 版本信息数组 [{ valueId: X, version: X }, ...]
 * @returns {Promise} 响应数据
 */
export async function getVersion(params) {
  return await request('/cdtdmExcel/share/getVersion', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 15. 判断数据是否锁定
 * @param {Object} params - 参数对象
 * @param {number} params.value_id - 数据ID
 * @param {string} params.lock_user_id - 锁定用户ID
 * @returns {Promise} 响应数据
 * @returns {Object} data.lock - true 锁定成功可编辑，false 数据已被锁定
 * @returns {number} data.lockUserId - 锁定用户ID
 * @returns {string} data.lockMessage - 锁定消息
 */
export async function lockData(params) {
  return await request('/cdtdmExcel/share/lock', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 17. 初始化数据集演示数据
 * @returns {Promise} 响应数据
 */
export async function initDemoData() {
  return await request('/cdtdmExcel/share/initDemoData', {
    method: 'POST'
  })
}

/**
 * 18. 根据userOperatorId获取userId
 * @param {string} user_operator_id - 用户操作ID
 * @returns {Promise} 响应数据 { userId: number }
 */
export async function getUserIdByUserOperatorId(user_operator_id) {
  return await request(`/cdtdmExcel/getUserIdByUserOperatorId?user_operator_id=${user_operator_id}`, {
    method: 'GET'
  })
}

/**
 * 19. 获取可移交权限的成员列表
 * @param {Object} params - 参数对象
 * @param {number} params.database_id - 数据集ID
 * @param {number} params.group_id - 工作组ID
 * @returns {Promise} 响应数据 { userList: Array }
 */
export async function getTransferDatabaseUserList(params) {
  return await request(`/cdtdmExcel/getTransferDatabaseUserList?database_id=${params.database_id}&group_id=${params.group_id}`, {
    method: 'GET'
  })
}

/**
 * 20. 移交数据集管理权限
 * @param {Object} params - 参数对象
 * @param {number} params.share_database_id - 数据集ID
 * @param {number} params.user_id - 新拥有者用户ID
 * @returns {Promise} 响应数据
 */
export async function transferDatabaseOwner(params) {
  return await request('/cdtdmExcel/share/transferDatabaseOwner', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

/**
 * 21. 通过分享码获取数据集基本信息（需验证权限）
 * @param {string} shareCode - 分享码
 * @param {string} userId - 当前用户ID
 * @returns {Promise} 响应数据 { databaseId, databaseName, groupId, shareCode }
 */
export async function getDatasetByShareCode(shareCode, userId) {
  return await request(`/cdtdmExcel/share/getDatasetByShareCode?share_code=${shareCode}&user_id=${userId}`, {
    method: 'GET'
  })
}

